/**
 * Experience Mode Overlay Component
 * 
 * Main overlay component that displays the experience mode interface
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useExperienceMode } from './ExperienceModeProvider';
import { ExperienceSpotIcon } from './ExperienceSpotIcon';
import { ExperienceVideoPlayer } from './ExperienceVideoPlayer';
import { useSpotPositions } from './hooks/useSpotPositions';
import { AdminExperienceEditor } from './AdminExperienceEditor';
import { auth } from '../../firebase';
import { SpotLayout, LAYOUT_BREAKPOINTS, LayoutPosition, ExperienceSpot } from '../../types/experienceMode.types';

interface ExperienceModeOverlayProps {
  viewMode?: 'admin' | 'member';
}

export const ExperienceModeOverlay: React.FC<ExperienceModeOverlayProps> = ({ viewMode = 'member' }) => {
  const { state, deactivateExperienceMode, playVideo, refreshProgress, activateExperienceMode } = useExperienceMode();
  const { calculatePositions } = useSpotPositions();
  const [isClosing, setIsClosing] = useState(false);
  const [spotsWithPositions, setSpotsWithPositions] = useState(state.spots);
  const [isAdminMode, setIsAdminMode] = useState(false);
  
  // Check if current user is admin
  const adminEmails = useMemo(() => [
    'info@superkonnected.nl', 
    'support@superkonnected.nl',
    'bussinesssecurit@gmail.com'
  ], []);
  
  const isAdmin = useMemo(() => {
    const email = auth.currentUser?.email;
    const isAdminUser = email && adminEmails.includes(email);
    // Debug logging
    if (email) {
      console.log('🔍 Experience Mode Admin Check:', { email, isAdminUser, adminEmails });
    }
    return isAdminUser;
  }, [auth.currentUser?.email, adminEmails]);

  // Track spots-layer height so it covers the full document
  const [docHeight, setDocHeight] = useState(0);
  const [activeLayout, setActiveLayout] = useState<SpotLayout>('desktop');

  // Detect which layout applies based on viewport width
  const detectLayout = useCallback((): SpotLayout => {
    const w = window.innerWidth;
    if (w < LAYOUT_BREAKPOINTS.tablet) return 'mobile';
    if (w < LAYOUT_BREAKPOINTS.desktop) return 'tablet';
    return 'desktop';
  }, []);

  // Scale a stored position to the current document dimensions
  const scalePosition = useCallback((pos: { x: number; y: number; refWidth?: number; refHeight?: number }): { top: string; left: string } => {
    const currentW = document.documentElement.scrollWidth;
    const currentH = document.documentElement.scrollHeight;
    
    // If we have reference dimensions, scale proportionally
    if (pos.refWidth && pos.refWidth > 0) {
      const scaleX = currentW / pos.refWidth;
      // Only scale Y if refHeight is available; Y position depends more on layout flow
      // so we primarily scale X (horizontal shift) and keep Y as-is or scale with height
      const scaleY = pos.refHeight && pos.refHeight > 0 ? currentH / pos.refHeight : 1;
      return {
        top: `${pos.y * scaleY}px`,
        left: `${pos.x * scaleX}px`,
      };
    }
    
    // No reference dimensions — use raw pixels
    return { top: `${pos.y}px`, left: `${pos.x}px` };
  }, []);

  // Resolve the best position for a spot given the current layout
  const resolvePosition = useCallback((spot: ExperienceSpot, layout: SpotLayout): { top: string; left: string } | null => {
    // 1. Try multi-layout position for current layout
    const layoutPos = spot.layoutPositions?.[layout];
    if (layoutPos) {
      return scalePosition(layoutPos);
    }
    
    // 2. Fallback chain: desktop → tablet → mobile (prefer larger layouts)
    const fallbackOrder: SpotLayout[] = layout === 'mobile' 
      ? ['tablet', 'desktop'] 
      : layout === 'tablet' 
        ? ['desktop', 'mobile'] 
        : ['tablet', 'mobile'];
    
    for (const fb of fallbackOrder) {
      const fbPos = spot.layoutPositions?.[fb];
      if (fbPos) {
        return scalePosition(fbPos);
      }
    }
    
    // 3. Legacy fixedPosition — use percentages if available
    if (spot.fixedPosition) {
      if (spot.fixedPosition.xPercent != null && spot.fixedPosition.yPercent != null) {
        const docW = document.documentElement.scrollWidth;
        const docH = document.documentElement.scrollHeight;
        return {
          top: `${(spot.fixedPosition.yPercent / 100) * docH}px`,
          left: `${(spot.fixedPosition.xPercent / 100) * docW}px`,
        };
      }
      // Raw pixels with refWidth scaling
      if (spot.fixedPosition.x != null && spot.fixedPosition.y != null) {
        return scalePosition({
          x: spot.fixedPosition.x,
          y: spot.fixedPosition.y,
          refWidth: spot.fixedPosition.refWidth,
          refHeight: spot.fixedPosition.refHeight,
        });
      }
    }
    
    return null;
  }, [scalePosition]);

  // Add body class so CSS can override quiet-lounge hiding of info spots
  useEffect(() => {
    if (state.isActive) {
      document.body.classList.add('experience-mode-active');
    } else {
      document.body.classList.remove('experience-mode-active');
    }
    return () => {
      document.body.classList.remove('experience-mode-active');
    };
  }, [state.isActive]);

  // Calculate positions when overlay mounts or window resizes
  useEffect(() => {
    if (!state.isActive) return;
    
    const updatePositions = () => {
      // Update document height for spots-layer
      setDocHeight(document.documentElement.scrollHeight);
      
      // Detect active layout
      const layout = detectLayout();
      setActiveLayout(layout);
      
      const updated = state.spots.map(spot => {
        // Try multi-layout + legacy positioning
        const pos = resolvePosition(spot, layout);
        if (pos) {
          return { ...spot, iconPosition: pos };
        }
        
        // Element selector fallback
        if (spot.elementSelector) {
          const calculated = calculatePositions([spot]);
          return calculated[0] || spot;
        }
        return spot;
      });
      setSpotsWithPositions(updated);
    };
    
    // Initial calculation
    updatePositions();
    
    // Recalculate on resize (layout change + docHeight update)
    window.addEventListener('resize', updatePositions);
    
    return () => {
      window.removeEventListener('resize', updatePositions);
    };
  }, [state.spots, state.isActive, calculatePositions, detectLayout, resolvePosition]);

  // Handle ESC key to close overlay (only when no video playing)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !state.isPlayingVideo) {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [state.isPlayingVideo]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      deactivateExperienceMode();
      setIsClosing(false);
    }, 300);
  };

  const handleAdminModeToggle = async () => {
    if (isAdminMode) {
      // Refresh spots when exiting admin mode
      await activateExperienceMode(state.currentPageId!);
      await refreshProgress();
    }
    setIsAdminMode(!isAdminMode);
  };

  // Apply grayscale to main content area (not app-root) when experience mode is active
  // This prevents the filter from affecting buttons that are outside the content area
  // MUST be before any early returns (React Hooks rule)
  useEffect(() => {
    const mainContent = document.querySelector('.mobile-content-wrapper');
    
    if (!state.isActive || state.isPlayingVideo) {
      // Remove grayscale when inactive OR when playing video
      if (mainContent) {
        (mainContent as HTMLElement).style.filter = '';
        (mainContent as HTMLElement).style.transition = 'filter 0.3s ease';
      }
      return;
    }
    
    // Apply grayscale when active and NOT playing video
    if (mainContent) {
      (mainContent as HTMLElement).style.filter = 'grayscale(100%)';
      (mainContent as HTMLElement).style.transition = 'filter 0.3s ease';
    }
    
    return () => {
      if (mainContent) {
        (mainContent as HTMLElement).style.filter = '';
        (mainContent as HTMLElement).style.transition = '';
      }
    };
  }, [state.isActive, state.isPlayingVideo]);

  // --- Info Spots: track positions of homepage 'i' buttons ---
  const [infoSpotPositions, setInfoSpotPositions] = useState<Record<string, { top: number; left: number }>>({});
  const [activeInfoSpot, setActiveInfoSpot] = useState<string | null>(null);
  const infoCardRef = useRef<HTMLDivElement>(null);

  // Info spot descriptions for homepage sections
  const infoSpotData: Record<string, { label: string; title: string; items: string[] }> = useMemo(() => ({
    sidebar: {
      label: 'Sidebar',
      title: 'Sidebar uitleg',
      items: [
        'Feeds — Snelle toegang en statistieken van je feeds.',
        'Display Instellingen — Pas je visuele ervaring aan.',
        'Clean Mind Filter — Filter content voor een positieve ervaring.',
        'Networkclub Finder — Vind en connect met je netwerk club.',
      ],
    },
    carousel: {
      label: 'Carrousel',
      title: 'Carrousel uitleg',
      items: [
        "Video's — Community video content van ondernemers.",
        'Events — Aankomende netwerk events en bijeenkomsten.',
        'Ondernemers Online — Wie is er nu online.',
        'Collaborations — Actieve samenwerkingen in je netwerk.',
        'Club Nieuws — Laatste updates van je netwerkclubs.',
      ],
    },
    newsfeed: {
      label: 'Nieuwsfeed',
      title: 'Nieuwsfeed uitleg',
      items: [
        'Posts worden gesorteerd op tijd — nieuwste bovenaan.',
        'Respect en Mutual Respect versterken zichtbaarheid.',
        'Geen likes of scores — alleen echte interactie telt.',
        'AI Visibility Impact analyseert netwerkgedrag.',
        "Gedeelde video's krijgen een visibilityBoost.",
      ],
    },
  }), []);

  // Calculate info spot positions from DOM
  useEffect(() => {
    if (!state.isActive) return;

    const updateInfoSpotPositions = () => {
      const positions: Record<string, { top: number; left: number }> = {};
      document.querySelectorAll('[data-info-spot]').forEach((el) => {
        const id = el.getAttribute('data-info-spot');
        if (!id) return;
        const rect = el.getBoundingClientRect();
        positions[id] = {
          top: rect.top + window.scrollY + rect.height / 2,
          left: rect.left + window.scrollX + rect.width / 2,
        };
      });
      setInfoSpotPositions(positions);
    };

    // Small delay to let DOM settle
    const timer = setTimeout(updateInfoSpotPositions, 300);
    window.addEventListener('resize', updateInfoSpotPositions);
    window.addEventListener('scroll', updateInfoSpotPositions);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateInfoSpotPositions);
      window.removeEventListener('scroll', updateInfoSpotPositions);
    };
  }, [state.isActive, state.spots]);

  // Close info card on click outside
  useEffect(() => {
    if (!activeInfoSpot) return;
    const handleClick = (e: MouseEvent) => {
      if (infoCardRef.current && !infoCardRef.current.contains(e.target as Node)) {
        setActiveInfoSpot(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeInfoSpot]);

  // Reset info state when overlay closes
  useEffect(() => {
    if (!state.isActive) {
      setActiveInfoSpot(null);
      setInfoSpotPositions({});
    }
  }, [state.isActive]);

  if (!state.isActive) return null;

  // Admin editor mode
  if (isAdminMode && isAdmin && viewMode === 'admin') {
    return (
      <AdminExperienceEditor
        pageId={state.currentPageId!}
        spots={state.spots}
        onClose={() => {
          setIsAdminMode(false);
          handleClose();
        }}
        onUpdate={async () => {
          await activateExperienceMode(state.currentPageId!);
          await refreshProgress();
        }}
      />
    );
  }

  return (
    <>
      {/* Experience Overlay — fixed viewport layer for backdrop + buttons */}
      {/* Hide overlay when playing video so video player is fully visible */}
      {!state.isPlayingVideo && (
        <div className={`experience-overlay ${isClosing ? 'closing' : ''}`}>
          {/* Backdrop - no grayscale filter, just click handler */}
          <div className="experience-backdrop" onClick={handleClose} />
        
        {/* Close button — stays fixed in viewport */}
        <button 
          className="experience-close-btn" 
          onClick={handleClose}
          aria-label="Close Experience Mode"
        >
          <span className="close-icon">✕</span>
          <span className="close-text">ESC</span>
        </button>
        
        {/* Admin Mode Toggle — stays fixed in viewport */}
        {isAdmin && viewMode === 'admin' && !state.isPlayingVideo && (
          <button
            className="experience-admin-toggle"
            onClick={handleAdminModeToggle}
            title="Admin Positioning Mode"
          >
            <span className="text-xl">🎯</span>
            <span className="text-sm font-semibold">Admin Mode</span>
          </button>
        )}
        </div>
      )}
      
      {/* 
        Spots layer — OUTSIDE the fixed overlay so spots scroll with page.
        Uses position:absolute covering the full document height.
        Hide when playing video
      */}
      {!state.isPlayingVideo && (
        <div
          className="experience-spots-layer"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            minHeight: '100%',
            height: `${docHeight}px`,
            pointerEvents: 'none',
            zIndex: 10001,
          }}
        >
          {/* Highlight cutouts */}
          {spotsWithPositions.map(spot => (
            spot.position && (
              <div
                key={`highlight-${spot.id}`}
                className="highlight-cutout"
                style={{
                  position: 'absolute',
                  top: spot.position.top,
                  left: spot.position.left,
                  width: spot.position.width,
                  height: spot.position.height,
                  pointerEvents: 'none',
                }}
              />
            )
          ))}
          
          {/* Floating video icons — absolute, scroll with page */}
          {spotsWithPositions.map((spot, index) => (
            spot.iconPosition && (
              <ExperienceSpotIcon
                key={spot.id}
                spot={spot}
                index={index}
                onClick={() => playVideo(spot)}
                isVisited={state.progress?.seenSpots.includes(spot.id) || false}
              />
            )
          ))}

          {/* Info Spots — positioned at existing 'i' buttons */}
          {Object.entries(infoSpotPositions).map(([id, pos]) => {
            const data = infoSpotData[id];
            if (!data) return null;
            return (
              <button
                key={`info-spot-${id}`}
                className="experience-info-spot"
                style={{
                  position: 'absolute',
                  top: `${pos.top}px`,
                  left: `${pos.left}px`,
                  pointerEvents: 'auto',
                }}
                onClick={() => setActiveInfoSpot(activeInfoSpot === id ? null : id)}
                aria-label={`Info: ${data.label}`}
              >
                <span className="info-spot-indicator">i</span>
                <span className="info-spot-label">{data.label}</span>
              </button>
            );
          })}

          {/* Active info card — rendered on overlay layer */}
          {activeInfoSpot && infoSpotPositions[activeInfoSpot] && infoSpotData[activeInfoSpot] && (
            <div
              ref={infoCardRef}
              className="experience-info-card"
              style={{
                position: 'absolute',
                top: `${infoSpotPositions[activeInfoSpot].top + 24}px`,
                left: `${Math.min(infoSpotPositions[activeInfoSpot].left, window.innerWidth - 310)}px`,
                pointerEvents: 'auto',
              }}
            >
              <div className="info-card-header">
                <span className="info-card-title">{infoSpotData[activeInfoSpot].title}</span>
                <button
                  className="info-card-close"
                  onClick={() => setActiveInfoSpot(null)}
                >
                  ×
                </button>
              </div>
              <ul className="info-card-list">
                {infoSpotData[activeInfoSpot].items.map((item, i) => (
                  <li key={i} className="info-card-item">
                    <span className="info-card-bullet" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Legend — fixed bottom-right, left of mini avatar */}
      {!state.isPlayingVideo && (
        <div className="experience-legend">
          <div className="legend-item">
            <span className="legend-video-icon">1</span>
            <span className="legend-text">Video uitleg</span>
          </div>
          <div className="legend-item">
            <span className="legend-info-icon">i</span>
            <span className="legend-text">Tekst uitleg</span>
          </div>
        </div>
      )}
      
      {/* Video Player - renders on top when playing */}
      {state.isPlayingVideo && state.activeSpot && (
        <ExperienceVideoPlayer spot={state.activeSpot} />
      )}
    </>
  );
};
